import { Download, Plus, Search } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ApiError, downloadText, exportEntries, type UserRole } from "@/lib/api";
import { useUserManagementViewModel } from "@/pages/dashboard/admin/useUserManagementViewModel";

export function UserManagementView() {
  const {
    user, isAdmin,
    usersPage, setUsersPage, usersPageSize, setUsersPageSize, usersSearch, setUsersSearch,
    newUser, setNewUser,
    usersData, usersCountData,
    createUserMutation, toggleUserMutation, deleteUserMutation,
    deleteDialogOpen, setDeleteDialogOpen, userToDelete, setUserToDelete, deleteExported, setDeleteExported,
  } = useUserManagementViewModel();
  return (
    <>
    <div className="mt-4 grid grid-cols-1 gap-4 [&>*]:min-w-0">
                <Card>
                  <CardHeader>
                    <CardTitle>Employee & Contractor Management</CardTitle>
                    <CardDescription>Create scoped dashboard accounts and toggle access. {usersCountData?.count ?? 0} total.</CardDescription>
                  </CardHeader>
                  <CardContent className="grid gap-4">
                    <form
                      className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_1fr_1fr_150px_auto]"
                      onSubmit={(event) => {
                        event.preventDefault();
                        if (newUser.email && newUser.password && newUser.full_name) createUserMutation.mutate();
                      }}
                    >
                      <Input value={newUser.full_name} onChange={(event) => setNewUser({ ...newUser, full_name: event.target.value })} placeholder="Full name" />
                      <Input type="email" value={newUser.email} onChange={(event) => setNewUser({ ...newUser, email: event.target.value })} placeholder="Email" />
                      <Input type="password" value={newUser.password} onChange={(event) => setNewUser({ ...newUser, password: event.target.value })} placeholder="Password" autoComplete="new-password" />
                      <Select value={newUser.role} onValueChange={(role) => setNewUser({ ...newUser, role: role as Exclude<UserRole, "admin" | "manager" | "readonly_admin"> })}>
                        <SelectTrigger>
                          <SelectValue />
                          <SelectContent>
                            {/* <SelectItem value="employee">Employee</SelectItem> */}
                            <SelectItem value="subcontractor">Subcontractor</SelectItem>
                          </SelectContent>
                        </SelectTrigger>
                      </Select>
                      <Button type="submit" disabled={createUserMutation.isPending}>
                        <Plus className="size-4" />
                        Add
                      </Button>
                    </form>
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-3 top-3 size-4 text-muted-foreground" />
                      <Input className="pl-9" value={usersSearch} onChange={(event) => { setUsersSearch(event.target.value); setUsersPage(0); }} placeholder="Search employees / contractors" />
                    </div>
                    <div className="grid gap-2">
                      <div className="text-sm font-semibold text-muted-foreground">Active</div>
                      {(usersData ?? []).reduce((acc: any[], u: any) => { if (u.is_active) acc.push(u); return acc; }, []).map((managedUser) => (
                        <div key={managedUser.id} className="grid gap-2 rounded-lg border p-3 md:grid-cols-[1fr_120px_100px_80px] md:items-center">
                          <div>
                            <div className="text-sm font-medium">{managedUser.full_name}</div>
                            <div className="text-xs text-muted-foreground">{managedUser.email} • {managedUser.company_name}{(managedUser as any).co_code ? ` • Co: ${(managedUser as any).co_code}` : ''}{(managedUser as any).office_location ? ` • Loc: ${(managedUser as any).office_location}` : ''}</div>
                          </div>
                          <Badge variant="outline">{managedUser.role}</Badge>
                          <Switch
                            checked={managedUser.is_active}
                            disabled={managedUser.id === user?.id}
                            onCheckedChange={(checked) => toggleUserMutation.mutate({ id: managedUser.id, is_active: checked })}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                            disabled={managedUser.id === user?.id || managedUser.role === "admin"}
                            onClick={() => {
                              setUserToDelete(managedUser);
                              setDeleteExported(false);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            Delete
                          </Button>
                        </div>
                      ))}
                      {(usersData ?? []).reduce((acc: any[], u: any) => { if (!u.is_active) acc.push(u); return acc; }, []).length > 0 && (
                        <>
                          <div className="mt-2 text-sm font-semibold text-muted-foreground">Inactive / Hidden</div>
                          {(usersData ?? []).reduce((acc: any[], u: any) => { if (!u.is_active) acc.push(u); return acc; }, []).map((managedUser) => (
                            <div key={managedUser.id} className="grid gap-2 rounded-lg border border-dashed p-3 md:grid-cols-[1fr_120px_100px_80px] md:items-center bg-muted/30">
                              <div>
                                <div className="text-sm font-medium">{managedUser.full_name}</div>
                                <div className="text-xs text-muted-foreground">{managedUser.email} • {managedUser.company_name}</div>
                                {managedUser.invite_send_on && !managedUser.invite_sent_at ? (
                                  <div className="text-xs text-muted-foreground">Invite scheduled for {managedUser.invite_send_on}</div>
                                ) : managedUser.invite_sent_at && !managedUser.is_active ? (
                                  <div className="text-xs text-muted-foreground">Invite sent, awaiting password</div>
                                ) : null}

                              </div>
                              <Badge variant="outline">{managedUser.role}</Badge>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={managedUser.id === user?.id}
                                onClick={() => toggleUserMutation.mutate({ id: managedUser.id, is_active: true })}
                              >
                                Reactivate
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                                disabled={managedUser.id === user?.id || managedUser.role === "admin"}
                                onClick={() => {
                                  setUserToDelete(managedUser);
                                  setDeleteExported(false);
                                  setDeleteDialogOpen(true);
                                }}
                              >
                                Delete
                              </Button>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => setUsersPage((p) => Math.max(0, p - 1))} disabled={usersPage === 0}>
                          Previous
                        </Button>
                        <span className="text-sm text-muted-foreground">
                          Page {usersPage + 1} of {Math.max(1, Math.ceil((usersCountData?.count ?? 0) / usersPageSize))}
                        </span>
                        <Button variant="outline" size="sm" onClick={() => setUsersPage((p) => p + 1)} disabled={(usersData ?? []).length < usersPageSize}>
                          Next
                        </Button>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Show</span>
                        <Select value={String(usersPageSize)} onValueChange={(v) => { setUsersPageSize(Number(v)); setUsersPage(0); }}>
                          <SelectTrigger className="h-8 w-20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[10, 25, 50, 100].map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <span className="text-sm text-muted-foreground">per page</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

    </div>
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User Permanently</DialogTitle>
            <DialogDescription>
              You are about to permanently delete <strong>{userToDelete?.full_name}</strong> ({userToDelete?.email}).
              This will also remove all of their local punch records.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="rounded-lg border border-warning/20 bg-warning/10 p-3 text-sm text-warning">
              <strong>Warning:</strong> This action cannot be undone. Please export their punches first to ensure you have a backup.
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="w-full"
                onClick={async () => {
                  if (!userToDelete) return;
                  const params = new URLSearchParams();
                  params.set("user_id", String(userToDelete.id));
                  try {
                    const csv = await exportEntries(params);
                    downloadText(`punches_${userToDelete.full_name.replace(/\s+/g, "_")}.csv`, csv, "text/csv;charset=utf-8");
                    setDeleteExported(true);
                  } catch (error) {
                    // silent for this one, or could set state but keep minimal
                    console.error("Export for delete failed", error);
                  }
                }}
              >
                <Download className="mr-2 size-4" />
                Export Punches First
              </Button>
            </div>
            {deleteExported && (
              <div className="text-xs text-success">Export downloaded. You may now proceed with deletion.</div>
            )}
            {deleteUserMutation.isError && (
              <div className="text-xs text-destructive">
                {deleteUserMutation.error instanceof ApiError
                  ? deleteUserMutation.error.message
                  : "Delete failed."}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!deleteExported || deleteUserMutation.isPending}
              onClick={() => {
                if (userToDelete) deleteUserMutation.mutate(userToDelete.id);
              }}
            >
              {deleteUserMutation.isPending ? "Deleting…" : "Permanently Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </>
  );
}
